import { PC24_CREDIT_IMPORT_FAILURE_REASONS, PC24_CREDIT_IMPORT_STATUSES } from 'cpool-constants';
import { schemaId } from 'cpool-schemas';
import { z } from 'zod';

// Each row is one of three variants discriminated by `status`. Fields that
// should not carry data in a given state are typed as `z.null()` (literal)
// rather than omitted, so structural access stays valid for consumers while
// illegal combinations become unrepresentable at the type level.

const baseFields = {
    attemptCount: z.number().int().nonnegative().default(0),
    loanRequestId: schemaId,
};

export const pc24InFlightRowSchema = z.object({
    ...baseFields,
    datasetActionId: z.null().default(null),
    directEntryUrl: z.null().default(null),
    droppedLiabilityCount: z.number().int().nonnegative().default(0),
    entryUrl: z.null().default(null),
    failureReason: z.null().default(null),
    lastError: z.null().default(null),
    linkGeneratedAt: z.null().default(null),
    status: z.literal(PC24_CREDIT_IMPORT_STATUSES.IN_FLIGHT),
    succeededAt: z.null().default(null),
});

export const pc24SucceededRowSchema = z.object({
    ...baseFields,
    datasetActionId: z.string(),
    // Nullable: PC24 only returns directEntryUrl when the request sends targetProductId.
    directEntryUrl: z.string().nullable().default(null),
    droppedLiabilityCount: z.number().int().nonnegative().default(0),
    entryUrl: z.string(),
    failureReason: z.null().default(null),
    lastError: z.null().default(null),
    // Nullish: pre-Rev.-3 rows predate this column and are treated as stale by the open control.
    linkGeneratedAt: z.number().nullish().default(null),
    status: z.literal(PC24_CREDIT_IMPORT_STATUSES.SUCCEEDED),
    succeededAt: z.number(),
});

export const pc24FailedRowSchema = z.object({
    ...baseFields,
    datasetActionId: z.null().default(null),
    directEntryUrl: z.null().default(null),
    droppedLiabilityCount: z.number().int().nonnegative().default(0),
    entryUrl: z.null().default(null),
    failureReason: z.nativeEnum(PC24_CREDIT_IMPORT_FAILURE_REASONS),
    lastError: z.string().nullable().default(null),
    linkGeneratedAt: z.null().default(null),
    status: z.literal(PC24_CREDIT_IMPORT_STATUSES.FAILED),
    succeededAt: z.null().default(null),
});

export const Pc24CreditImportsTableDefinitionZod = z.discriminatedUnion('status', [
    pc24InFlightRowSchema,
    pc24SucceededRowSchema,
    pc24FailedRowSchema,
]);

export type Pc24CreditImportsTableSchema = z.infer<typeof Pc24CreditImportsTableDefinitionZod>;
export type Pc24InFlightRow = z.infer<typeof pc24InFlightRowSchema>;
export type Pc24SucceededRow = z.infer<typeof pc24SucceededRowSchema>;
export type Pc24FailedRow = z.infer<typeof pc24FailedRowSchema>;
